/**
 * Campo de upload da landing: sobe o arquivo e guarda a URL relativa devolvida.
 *
 * Extraído de `LandingEditorPanel.tsx`, onde nasceu, para o editor visual novo
 * (Puck) reaproveitá-lo como campo `custom` em vez de ganhar uma segunda
 * implementação de upload. Enquanto os dois editores conviverem, os dois
 * apontam para este arquivo.
 *
 * A URL guardada é relativa (`/uploads/landing/...`), então a config continua
 * válida em qualquer host — dev, produção, ou a landing servida por outro nome.
 */

import { useRef, useState } from 'react'
import { FiLoader, FiUpload } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import { encolherImagem } from '../lib/encolherImagem'

export default function CampoImagem({ value, onChange, label, ajuda }: {
  value: string
  onChange: (v: string) => void
  /**
   * Nome do campo, desenhado aqui dentro.
   *
   * Num campo `custom` o Puck entrega ao `render` só
   * `{field, name, id, value, onChange, readOnly}` e **não põe nada em volta** —
   * quem tem de desenhar o rótulo é o próprio render. Sem isto, todo campo de
   * imagem da coluna lateral fica SEM NOME: dentro de "Aparência e posição"
   * apareciam dois botões "Enviar" idênticos, um da imagem de fundo e outro do
   * ladrilho da estampa, e nada na tela dizendo qual era qual.
   *
   * Opcional porque o editor antigo já desenha o próprio `<Rotulo>` em volta;
   * passar ali duplicaria o nome.
   */
  label?: string
  /** Frase curta de apoio, para campos que se confundem entre si. */
  ajuda?: string
}) {
  const { authenticatedFetch } = useAuth()
  const ref = useRef<HTMLInputElement>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const enviar = async (file: File) => {
    setEnviando(true); setErro(null)
    try {
      const fd = new FormData()
      fd.append('image', await encolherImagem(file))
      // Sem Content-Type manual: o authenticatedFetch detecta FormData e deixa
      // o browser montar o boundary.
      const r = await authenticatedFetch(API_ENDPOINTS.LANDING_UPLOAD, { method: 'POST', body: fd })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'falha no upload')
      onChange(j.url)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha no upload')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <div>
          <span className="text-[11px] font-medium text-foreground">{label}</span>
          {ajuda && <div className="mt-0.5 text-[10px] text-muted-foreground">{ajuda}</div>}
        </div>
      )}
      <div className="flex items-center gap-2">
        {value ? (
          <img src={value} alt="" className="h-10 w-14 rounded border border-border object-cover" />
        ) : (
          <div className="h-10 w-14 rounded border border-dashed border-border" />
        )}
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={enviando}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          {enviando ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiUpload className="w-3 h-3" />}
          {value ? 'Trocar' : 'Enviar'}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[11px] text-muted-foreground hover:text-red-400 transition-colors"
          >
            remover
          </button>
        )}
      </div>
      <input
        ref={ref} type="file" className="hidden"
        accept="image/*,video/mp4"
        onChange={e => { const f = e.target.files?.[0]; if (f) enviar(f); e.target.value = '' }}
      />
      {value && <div className="truncate text-[10px] text-muted-foreground">{value}</div>}
      {erro && <div className="text-[10px] text-red-400">{erro}</div>}
    </div>
  )
}
